import { useMenuTree } from '@/features/menu/hooks/useMenu';
import { loc } from '@/utils/localized';
import { productCodeKey } from '@restaurant/types';
import { useMemo } from 'react';

/**
 * Which dish already holds a code, answered while typing from the menu tree the
 * list page cached. That tree carries exactly the codes the unique index defends
 * (it drops archived products, and archiving clears codes).
 *
 * Advisory — the cache goes stale, so the server's 409 is still the last word.
 */
export function useProductCodeConflict(
  menuId: string | null | undefined,
  excludeProductId?: string,
  /** Fetch only while the code field is open — a deep link should not pull a
   *  whole menu tree for nothing. */
  enabled = true,
) {
  const { data: tree, isPending } = useMenuTree(enabled ? menuId : undefined);

  const takenBy = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of tree?.categories ?? []) {
      for (const product of category.products) {
        if (product.id === excludeProductId) continue;
        const key = productCodeKey(product.code);
        if (key) map.set(key, loc(product.name));
      }
    }
    return map;
  }, [tree, excludeProductId]);

  return {
    conflictWith: (raw: string): string | null => {
      const key = productCodeKey(raw);
      return key ? (takenBy.get(key) ?? null) : null;
    },
    /** False while the tree loads — do not block Save on an answer we lack. */
    ready: !enabled || !isPending,
  };
}
